const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;
export const MAX_DEVICE_ID = 2147483600; // keep within signed 32-bit range

const isPositiveInteger = (value) => Number.isInteger(value) && value > 0;

export const deviceIdToAddressId = (deviceId) => {
  if (deviceId == null) {
    throw new Error('deviceId is required');
  }

  if (typeof deviceId === 'number' && Number.isFinite(deviceId)) {
    const rounded = Math.floor(deviceId);
    if (isPositiveInteger(rounded)) return rounded;
  }

  const str = String(deviceId).trim();
  if (!str) return 1;

  const numeric = Number(str);
  if (isPositiveInteger(numeric)) return numeric;

  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = (hash * FNV_PRIME) >>> 0;
  }

  const value = (hash % MAX_DEVICE_ID) + 1;
  return value;
};

const ensureUint8Array = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Uint8Array) {
    return value;
  }

  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }

  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }

  return null;
};

export const bytesToBase64 = (input) => {
  const bytes = ensureUint8Array(input);
  if (!bytes) {
    return null;
  }

  if (typeof Buffer !== 'undefined' && typeof Buffer.from === 'function') {
    try {
      return Buffer.from(bytes).toString('base64');
    } catch {}
  }

  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }

  if (typeof btoa === 'function') {
    try {
      return btoa(binary);
    } catch {}
  }

  if (typeof globalThis !== 'undefined' && typeof globalThis.btoa === 'function') {
    try {
      return globalThis.btoa(binary);
    } catch {}
  }

  return null;
};

const normalizeBase64 = (value) => {
  if (typeof value !== 'string') return '';
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4;
  if (padding === 0) return normalized;
  return normalized + '='.repeat(4 - padding);
};

const base64ToUint8Array = (value) => {
  if (!value) return new Uint8Array();
  const normalized = normalizeBase64(value);

  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(normalized, 'base64'));
  }

  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

export const base64ToBytes = (value) => base64ToUint8Array(value);

export const decodeBase64ToUtf8 = (value) => {
  const bytes = base64ToUint8Array(value);

  if (typeof TextDecoder !== 'undefined') {
    try {
      return new TextDecoder().decode(bytes);
    } catch {}
  }

  let result = '';
  for (let i = 0; i < bytes.length; i += 1) {
    result += String.fromCharCode(bytes[i]);
  }

  try {
    return decodeURIComponent(escape(result));
  } catch {
    return result;
  }
};

export const utf8ToUint8Array = (value) => {
  if (value == null) return new Uint8Array();
  if (value instanceof Uint8Array) return value;
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(String(value));
  }
  const str = String(value);
  const bytes = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i += 1) {
    bytes[i] = str.charCodeAt(i) & 0xff;
  }
  return bytes;
};

export const randomUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const cryptoRef =
    (typeof globalThis !== 'undefined' && globalThis.crypto) ||
    (typeof window !== 'undefined' ? window.crypto : null);

  if (!cryptoRef?.getRandomValues) {
    console.error('[E2EE] crypto.getRandomValues unavailable for UUID generation');
    throw new Error('Secure random UUID generation unavailable');
  }

  const bytes = new Uint8Array(16);
  cryptoRef.getRandomValues(bytes);

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const toHex = (n) => n.toString(16).padStart(2, '0');
  return (
    `${toHex(bytes[0])}${toHex(bytes[1])}${toHex(bytes[2])}${toHex(bytes[3])}-`
    + `${toHex(bytes[4])}${toHex(bytes[5])}-`
    + `${toHex(bytes[6])}${toHex(bytes[7])}-`
    + `${toHex(bytes[8])}${toHex(bytes[9])}-`
    + `${toHex(bytes[10])}${toHex(bytes[11])}${toHex(bytes[12])}${toHex(bytes[13])}${toHex(bytes[14])}${toHex(bytes[15])}`
  );
};
